
/* =====================================================
   Eyal Mart - firebase.js
   Firebase Auth + Firestore + Storage
   Video Posts: Firebase Storage
   No Cloudinary
===================================================== */


/* =====================================================
   🔥 FIREBASE IMPORTS
===================================================== */

import { initializeApp } from "firebase/app";

import {
  getFunctions,
  connectFunctionsEmulator,
} from "firebase/functions";

import {
  getAuth,
  GoogleAuthProvider,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  setPersistence,
  browserLocalPersistence,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";

import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  getDocs,
  addDoc,
  deleteDoc,
  query,
  collection,
  where,
  limit,
  orderBy,
  updateDoc,
  arrayUnion,
  arrayRemove,
  increment,
  serverTimestamp,
} from "firebase/firestore";

import { getMessaging } from "firebase/messaging";

import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";


/* =====================================================
   🔑 FIREBASE CONFIG
===================================================== */

const firebaseConfig = {

  apiKey: "AIzaSyBpdUOLQEITd6KhQn2Lw7vrUvOtprmpWiw",

  authDomain: "eyal--demo.firebaseapp.com",

  projectId: "eyal--demo",

  storageBucket: "eyal--demo.firebasestorage.app",

  messagingSenderId: "180805544983",

  appId: "1:180805544983:web:eab972f1bc04e3779f8052",

};


/* =====================================================
   🚀 INITIALIZE FIREBASE
===================================================== */

const app = initializeApp(firebaseConfig);


/* =====================================================
   🔔 FIREBASE CLOUD MESSAGING
===================================================== */

export const messaging = getMessaging(app);


/* =====================================================
   🔐 AUTH
===================================================== */

export const auth = getAuth(app);

export const provider =
  new GoogleAuthProvider();


/* =====================================================
   🔥 FIRESTORE
===================================================== */

export const db = getFirestore(app);


/* =====================================================
   ☁️ FIREBASE STORAGE
===================================================== */

export const storage = getStorage(app);


/* =====================================================
   ⚡ CLOUD FUNCTIONS
===================================================== */

export const functions =
  getFunctions(app, "us-central1");

// Uncomment only when using Firebase Emulator
// connectFunctionsEmulator(functions, "localhost", 5001);


/* =====================================================
   💾 PERSISTENT LOGIN
===================================================== */

setPersistence(
  auth,
  browserLocalPersistence
);


/* =====================================================
   📱 PHONE AUTH / OTP
===================================================== */

export const setupRecaptcha = () => {

  if (window.recaptchaVerifier) {
    return;
  }

  const container =
    document.getElementById(
      "recaptcha-container"
    );

  if (!container) {

    console.warn(
      "⚠️ reCAPTCHA container not found yet"
    );

    return;

  }

  window.recaptchaVerifier =
    new RecaptchaVerifier(
      auth,
      container,
      {
        size: "invisible",

        callback: () =>
          console.log(
            "✅ reCAPTCHA verified"
          ),

        "expired-callback": () =>
          console.warn(
            "⚠️ reCAPTCHA expired"
          ),

      }
    );

  window.recaptchaVerifier.render();

};


export const sendOTP = async (phone) => {

  try {

    if (!phone.match(/^\+91\d{10}$/)) {

      throw new Error(
        "Please enter a valid Indian number with +91"
      );

    }

    setupRecaptcha();

    const confirmation =
      await signInWithPhoneNumber(
        auth,
        phone,
        window.recaptchaVerifier
      );

    return confirmation;

  } catch (error) {

    console.error(
      "❌ OTP send error:",
      error
    );

    throw error;

  }

};


/* =====================================================
   👤 USER SETTINGS
===================================================== */

export const saveUserSettings = async (
  uid,
  data
) => {

  try {

    const userRef =
      doc(db, "users", uid);

    await setDoc(
      userRef,
      data,
      { merge: true }
    );

    const currentUser =
      auth.currentUser;

    if (currentUser && data.name) {

      await updateProfile(
        currentUser,
        {
          displayName: data.name,
        }
      );

    }

    console.log(
      "✅ User settings saved successfully to Firestore!"
    );

    return true;

  } catch (err) {

    console.error(
      "❌ Error saving user settings:",
      err
    );

    throw err;

  }

};


export const getUserSettings = async (
  uid
) => {

  const snap =
    await getDoc(
      doc(db, "users", uid)
    );

  return snap.exists()
    ? snap.data()
    : null;

};


/* =====================================================
   🎁 REFERRALS
===================================================== */

export const getUserByReferralCode = async (
  referralCode
) => {

  const q = query(

    collection(db, "users"),

    where(
      "referralCode",
      "==",
      referralCode
    ),

    limit(1)

  );

  const snap =
    await getDocs(q);

  if (snap.empty) {
    return null;
  }

  const docSnap =
    snap.docs[0];

  return {

    uid: docSnap.id,

    ...docSnap.data(),

  };

};


export const rewardReferral = async (
  newUserUid
) => {

  try {

    const newUser =
      await getUserSettings(
        newUserUid
      );

    if (!newUser) return;

    if (!newUser.referredByUid) {
      return;
    }

    if (newUser.referralRewarded) {
      return;
    }

    // Prevent self-referral
    if (
      newUser.referredByUid === newUserUid
    ) {

      return;

    }

    // Credit ₹5
    await creditWallet(

      newUser.referredByUid,

      5,

      `Referral Bonus - ${
        newUser.name || "New User"
      }`

    );

    // Increase referral count
    await updateDoc(

      doc(
        db,
        "users",
        newUser.referredByUid
      ),

      {
        referralCount: increment(1),
      }

    );

    // Mark as rewarded
    await updateDoc(

      doc(
        db,
        "users",
        newUserUid
      ),

      {
        referralRewarded: true,
      }

    );

    console.log(
      "✅ Referral reward credited."
    );

  } catch (err) {

    console.error(
      "Referral reward error:",
      err
    );

  }

};


/* =====================================================
   🔐 GOOGLE AUTH
===================================================== */

export const googleSignIn = async () => {

  await signInWithPopup(
    auth,
    provider
  );

  await auth.currentUser?.reload();

  return auth.currentUser;

};


export const googleLogout = () =>
  signOut(auth);


export const authListener = (cb) =>
  onAuthStateChanged(auth, cb);


/* =====================================================
   🖼️ ADD IMAGE / TEXT POST
===================================================== */

export const addPost = async (

  uid,

  text,

  imageUrl = "",

  area = "",

  userName,

  userPhotoURL,

  urlLink = ""

) => {

  const user =
    auth.currentUser;

  const nameToUse =
    userName ||
    user?.displayName ||
    "User";

  const photoToUse =
    userPhotoURL ||
    user?.photoURL ||
    "";

  const docRef =
    await addDoc(

      collection(db, "posts"),

      {

        uid,

        userName: nameToUse,

        userPhotoURL: photoToUse,

        text,

        imageUrl,

        area,

        urlLink,

        likes: [],

        createdAt:
          new Date().toISOString(),

      }

    );

  await updateDoc(

    docRef,

    {
      id: docRef.id,
    }

  );

  return docRef.id;

};


/* =====================================================
   📥 POSTS FEED
===================================================== */

export const getPostsFeed = async () => {

  const q = query(

    collection(db, "posts"),

    orderBy(
      "createdAt",
      "desc"
    )

  );

  const res =
    await getDocs(q);

  return res.docs.map((d) => ({

    id: d.id,

    ...d.data(),

  }));

};


export const getUserPosts = async (
  uid
) => {

  const list =
    await getPostsFeed();

  return list.filter(
    (p) => p.uid === uid
  );

};


export const deletePost = async (
  id
) => {

  return deleteDoc(
    doc(db, "posts", id)
  );

};


export const toggleLike = async (

  postId,

  uid,

  isLiked

) => {

  const postRef =
    doc(
      db,
      "posts",
      postId
    );

  if (isLiked) {

    await updateDoc(

      postRef,

      {
        likes: arrayRemove(uid),
      }

    );

  } else {

    await updateDoc(

      postRef,

      {
        likes: arrayUnion(uid),
      }

    );

  }

};


/* =====================================================
   🎬 VIDEO SYSTEM
   Firebase Storage + Firestore
===================================================== */


/* =====================================================
   🎬 1. UPLOAD VIDEO TO FIREBASE STORAGE
===================================================== */

export const uploadVideoToFirebase = (

  file,

  uid,

  onProgress

) => {

  return new Promise(

    (resolve, reject) => {

      try {

        // -----------------------------------------
        // VALIDATE FILE
        // -----------------------------------------

        if (!file) {

          reject(
            new Error(
              "No video selected."
            )
          );

          return;

        }

        if (!uid) {

          reject(
            new Error(
              "User is not logged in."
            )
          );

          return;

        }

        if (
          !file.type ||
          !file.type.startsWith("video/")
        ) {

          reject(
            new Error(
              "Please select a valid video file."
            )
          );

          return;

        }

        // Maximum 100 MB
        const maxSize =
          100 * 1024 * 1024;

        if (file.size > maxSize) {

          reject(
            new Error(
              "Video must be smaller than 100 MB."
            )
          );

          return;

        }

        // -----------------------------------------
        // UNIQUE FILE NAME
        // -----------------------------------------

        const extension =
          file.name
            .split(".")
            .pop() ||
          "mp4";

        const fileName =
          `video_${Date.now()}_${
            Math.random()
              .toString(36)
              .slice(2, 8)
          }.${extension}`;

        // -----------------------------------------
        // FIREBASE STORAGE PATH
        // -----------------------------------------

        const storageRef =
          ref(

            storage,

            `videos/${uid}/${fileName}`

          );

        // -----------------------------------------
        // START UPLOAD
        // -----------------------------------------

        const uploadTask =
          uploadBytesResumable(

            storageRef,

            file,

            {

              contentType:
                file.type,

              cacheControl:
                "public,max-age=3600",

            }

          );

        uploadTask.on(

          "state_changed",

          // ---------------------------------------
          // PROGRESS
          // ---------------------------------------

          (snapshot) => {

            const progress =
              Math.round(

                (

                  snapshot.bytesTransferred /

                  snapshot.totalBytes

                ) * 100

              );

            console.log(

              "🎬 Video upload:",

              progress + "%"

            );

            if (onProgress) {

              onProgress(progress);

            }

          },

          // ---------------------------------------
          // ERROR
          // ---------------------------------------

          (error) => {

            console.error(

              "❌ Firebase video upload failed:",

              error

            );

            reject(error);

          },

          // ---------------------------------------
          // SUCCESS
          // ---------------------------------------

          async () => {

            try {

              const videoUrl =
                await getDownloadURL(

                  uploadTask
                    .snapshot
                    .ref

                );

              const storagePath =
                uploadTask
                  .snapshot
                  .ref
                  .fullPath;

              console.log(

                "✅ Firebase video uploaded:",

                videoUrl

              );

              resolve({

                videoUrl,

                storagePath,

                fileName,

              });

            } catch (error) {

              console.error(

                "❌ Failed to get video URL:",

                error

              );

              reject(error);

            }

          }

        );

      } catch (error) {

        console.error(

          "❌ Video upload failed:",

          error

        );

        reject(error);

      }

    }

  );

};


/* =====================================================
   🎬 2. ADD VIDEO POST TO FIRESTORE
===================================================== */

export const addVideoPost = async (

  uid,

  caption = "",

  videoUrl = "",

  area = "",

  userName = "",

  userPhotoURL = "",

  storagePath = "",

  urlLink = ""

) => {

  try {

    if (!uid) {

      throw new Error(
        "User is not logged in."
      );

    }

    if (!videoUrl) {

      throw new Error(
        "Video URL is missing."
      );

    }

    const videoRef =
      doc(
        collection(db, "videos")
      );

    await setDoc(

      videoRef,

      {

        id: videoRef.id,

        uid,

        userName:
          userName || "User",

        userPhotoURL:
          userPhotoURL || "",

        caption:
          caption || "",

        videoUrl,

        storagePath:
          storagePath || "",

        area:
          area || "",

        urlLink:
          urlLink || "",

        likes: [],

        createdAt:
          new Date().toISOString(),

      }

    );

    console.log(

      "✅ Video post saved:",

      videoRef.id

    );

    return videoRef.id;

  } catch (error) {

    console.error(

      "❌ Error adding video post:",

      error

    );

    throw error;

  }

};


/* =====================================================
   📥 3. GET ALL VIDEOS FEED
===================================================== */

export const getVideosFeed = async () => {

  try {

    const videosRef =
      collection(db, "videos");

    const snapshot =
      await getDocs(videosRef);

    const videos =
      snapshot.docs.map(

        (videoDoc) => ({

          id: videoDoc.id,

          ...videoDoc.data(),

        })

      );

    // Newest first
    videos.sort((a, b) => {

      const dateA =
        new Date(
          a.createdAt || 0
        ).getTime();

      const dateB =
        new Date(
          b.createdAt || 0
        ).getTime();

      return dateB - dateA;

    });

    console.log(

      "✅ Videos loaded:",

      videos.length

    );

    return videos;

  } catch (error) {

    console.error(

      "❌ Error fetching videos:",

      error

    );

    throw error;

  }

};


/* =====================================================
   👤 4. GET USER VIDEOS
===================================================== */

export const getUserVideos = async (
  uid
) => {

  try {

    if (!uid) {

      return [];

    }

    const videosRef =
      collection(db, "videos");

    const q =
      query(

        videosRef,

        where(
          "uid",
          "==",
          uid
        )

      );

    const snapshot =
      await getDocs(q);

    const videos =
      snapshot.docs.map(

        (videoDoc) => ({

          id: videoDoc.id,

          ...videoDoc.data(),

        })

      );

    // Newest first
    videos.sort((a, b) => {

      const dateA =
        new Date(
          a.createdAt || 0
        ).getTime();

      const dateB =
        new Date(
          b.createdAt || 0
        ).getTime();

      return dateB - dateA;

    });

    console.log(

      "✅ User videos loaded:",

      videos.length

    );

    return videos;

  } catch (error) {

    console.error(

      "❌ Error fetching user videos:",

      error

    );

    throw error;

  }

};


/* =====================================================
   ❤️ 5. TOGGLE VIDEO LIKE
===================================================== */

export const toggleVideoLike = async (

  videoId,

  uid,

  isLiked

) => {

  try {

    if (!videoId || !uid) {

      throw new Error(

        "Video ID and user ID are required."

      );

    }

    const videoRef =
      doc(

        db,

        "videos",

        videoId

      );

    if (isLiked) {

      // Unlike
      await updateDoc(

        videoRef,

        {

          likes:
            arrayRemove(uid),

        }

      );

    } else {

      // Like
      await updateDoc(

        videoRef,

        {

          likes:
            arrayUnion(uid),

        }

      );

    }

    console.log(

      isLiked
        ? "💔 Video unliked"
        : "❤️ Video liked"

    );

    return true;

  } catch (error) {

    console.error(

      "❌ Error toggling video like:",

      error

    );

    throw error;

  }

};


/* =====================================================
   🗑️ 6. DELETE VIDEO POST
===================================================== */

export const deleteVideo = async (

  videoId,

  storagePath = ""

) => {

  try {

    if (!videoId) {

      throw new Error(
        "Video ID is missing."
      );

    }

    // -----------------------------------------
    // DELETE FIREBASE STORAGE FILE
    // -----------------------------------------

    if (storagePath) {

      try {

        const fileRef =
          ref(

            storage,

            storagePath

          );

        await deleteObject(
          fileRef
        );

        console.log(

          "✅ Firebase Storage video deleted."

        );

      } catch (storageError) {

        console.warn(

          "⚠️ Storage file not deleted:",

          storageError.message

        );

      }

    }

    // -----------------------------------------
    // DELETE FIRESTORE VIDEO DOCUMENT
    // -----------------------------------------

    await deleteDoc(

      doc(

        db,

        "videos",

        videoId

      )

    );

    console.log(

      "✅ Firestore video deleted:",

      videoId

    );

    return true;

  } catch (error) {

    console.error(

      "❌ Error deleting video:",

      error

    );

    throw error;

  }

};


/* =====================================================
   💰 WALLET
===================================================== */

export const creditWallet = async (

  uid,

  amount,

  reason,

  submissionId = ""

) => {

  const walletRef =
    doc(
      db,
      "wallets",
      uid
    );

  const snap =
    await getDoc(walletRef);

  if (!snap.exists()) {

    await setDoc(

      walletRef,

      {

        balance: amount,

        totalEarned: amount,

        totalWithdrawn: 0,

        updatedAt:
          serverTimestamp(),

      }

    );

  } else {

    await updateDoc(

      walletRef,

      {

        balance:
          increment(amount),

        totalEarned:
          increment(amount),

        updatedAt:
          serverTimestamp(),

      }

    );

  }

  await addDoc(

    collection(
      db,
      "walletTransactions"
    ),

    {

      uid,

      amount,

      type: "credit",

      reason,

      submissionId,

      createdAt:
        serverTimestamp(),

    }

  );

};


export const getWallet = async (
  uid
) => {

  const snap =
    await getDoc(

      doc(
        db,
        "wallets",
        uid
      )

    );

  if (!snap.exists()) {

    return {

      balance: 0,

      totalEarned: 0,

      totalWithdrawn: 0,

    };

  }

  return snap.data();

};


export const getWalletTransactions = async (
  uid
) => {

  const q =
    query(

      collection(
        db,
        "walletTransactions"
      ),

      orderBy(
        "createdAt",
        "desc"
      )

    );

  const res =
    await getDocs(q);

  return res.docs

    .map((d) => ({

      id: d.id,

      ...d.data(),

    }))

    .filter(
      (t) => t.uid === uid
    );

};


/* =====================================================
   🏆 CREATOR PROGRAM
===================================================== */

export const joinCreatorProgram = async (
  uid
) => {

  await setDoc(

    doc(
      db,
      "users",
      uid
    ),

    {

      creator: true,

      creatorJoinedAt:
        serverTimestamp(),

    },

    {
      merge: true,
    }

  );

};


export const isCreator = async (
  uid
) => {

  const snap =
    await getDoc(

      doc(
        db,
        "users",
        uid
      )

    );

  if (!snap.exists()) {

    return false;

  }

  return snap.data().creator === true;

};